/*
 * Lead form: validation + AJAX submission + in-place success swap.
 *
 * Replaces the previous Bootstrap `needs-validation` flow. The server is the
 * source of truth for validation; this module provides instant feedback,
 * sends to the REST endpoint, and renders server errors / success in place.
 */

const CONTAINER_ID = 'insuranceFormContainer';
const FORM_ID = 'insuranceForm';
const FORM_ERROR_ID = 'si-form-error';
const POLICIES_FEEDBACK_ID = 'policiesInvalidFeedback';
const POLICIES_CONTAINER_ID = 'policiesCheckboxes';

function getField(form, name) {
	return form.querySelector(`[name="${name}"]`);
}

function findFeedback(field) {
	if (!field) return null;
	const wrapper = field.closest('.form-floating, .form-check, .mb-3') || field.parentElement;
	return wrapper ? wrapper.querySelector('.invalid-feedback') : null;
}

function setFieldError(form, name, message) {
	if (name === 'policies') {
		const container = document.getElementById(POLICIES_CONTAINER_ID);
		const feedback = document.getElementById(POLICIES_FEEDBACK_ID);
		if (container) container.classList.add('policies-error');
		if (feedback) {
			feedback.style.display = 'block';
			if (message) feedback.textContent = message;
		}
		return container;
	}

	const field = getField(form, name);
	if (!field) return null;
	field.classList.add('is-invalid');
	const feedback = findFeedback(field);
	if (feedback && message) feedback.textContent = message;
	return field;
}

function clearFieldError(field) {
	if (!field) return;
	field.classList.remove('is-invalid');
}

function clearAllErrors(form) {
	form.querySelectorAll('.is-invalid').forEach((el) => el.classList.remove('is-invalid'));
	const policiesContainer = document.getElementById(POLICIES_CONTAINER_ID);
	const policiesFeedback = document.getElementById(POLICIES_FEEDBACK_ID);
	if (policiesContainer) policiesContainer.classList.remove('policies-error');
	if (policiesFeedback) policiesFeedback.style.display = '';
	const formError = document.getElementById(FORM_ERROR_ID);
	if (formError) {
		formError.classList.add('d-none');
		formError.textContent = '';
	}
}

function showFormError(message) {
	const el = document.getElementById(FORM_ERROR_ID);
	if (!el || !message) return;
	el.textContent = message;
	el.classList.remove('d-none');
}

/**
 * Client-side preflight. Returns true when the form is good enough to send.
 * Server is the authoritative validator.
 */
function preflight(form) {
	let firstInvalid = null;

	// Native HTML5 validation (required, type=email, pattern, min/max, etc.).
	const controls = form.querySelectorAll('input, select, textarea');
	controls.forEach((el) => {
		if (el.type === 'hidden' || el.disabled) return;
		if (el.willValidate && !el.checkValidity()) {
			el.classList.add('is-invalid');
			if (!firstInvalid) firstInvalid = el;
		}
	});

	// Policies — at least one checkbox checked.
	const policiesChecked = form.querySelectorAll('input[name="policies[]"]:checked').length > 0;
	if (!policiesChecked) {
		const container = setFieldError(form, 'policies');
		if (!firstInvalid && container) firstInvalid = container;
	}

	if (firstInvalid) {
		firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
		if (typeof firstInvalid.focus === 'function') {
			try { firstInvalid.focus({ preventScroll: true }); } catch (_) { firstInvalid.focus(); }
		}
		return false;
	}
	return true;
}

function buildPayload(form) {
	const fd = new FormData(form);
	// FormData skips unchecked checkboxes; handle them explicitly.
	const policies = Array.from(form.querySelectorAll('input[name="policies[]"]:checked')).map((el) => el.value);
	const autoEmailEl = form.querySelector('input[name="autoEmailCheck"]');
	const payload = {};
	fd.forEach((value, key) => {
		if (key === 'policies[]' || key === 'autoEmailCheck') return;
		payload[key] = value;
	});
	payload.policies = policies;
	payload.autoEmailCheck = autoEmailEl ? autoEmailEl.checked : false;
	return payload;
}

function setSubmittingState(form, submitting) {
	const btn = form.querySelector('button[type="submit"]');
	form.classList.toggle('si-submitting', submitting);
	if (!btn) return;
	if (submitting) {
		btn.dataset.originalLabel = btn.dataset.originalLabel || btn.innerHTML;
		btn.disabled = true;
		btn.innerHTML = '<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>' + (btn.dataset.loadingLabel || btn.dataset.originalLabel);
	} else {
		btn.disabled = false;
		if (btn.dataset.originalLabel) btn.innerHTML = btn.dataset.originalLabel;
	}
}

function swapForSuccess(container, html) {
	container.innerHTML = html;
	container.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function attachLiveErrorClearing(form) {
	form.addEventListener('input', (event) => {
		const target = event.target;
		if (target && target.classList && target.classList.contains('is-invalid')) {
			clearFieldError(target);
		}
	}, true);

	form.addEventListener('change', (event) => {
		const target = event.target;
		if (target && target.name === 'policies[]') {
			const checked = form.querySelectorAll('input[name="policies[]"]:checked').length > 0;
			if (checked) {
				const container = document.getElementById(POLICIES_CONTAINER_ID);
				const feedback = document.getElementById(POLICIES_FEEDBACK_ID);
				if (container) container.classList.remove('policies-error');
				if (feedback) feedback.style.display = '';
			}
		}
	}, true);
}

async function handleSubmit(event) {
	event.preventDefault();
	const form = event.currentTarget;
	const container = document.getElementById(CONTAINER_ID);
	const config = window.simplinsurSubmit;

	if (!config || !config.endpoint) {
		// Fallback: let native submission happen.
		form.removeEventListener('submit', handleSubmit);
		form.submit();
		return;
	}

	clearAllErrors(form);

	if (!preflight(form)) return;

	const payload = buildPayload(form);
	if (container && container.dataset && container.dataset.lang) {
		payload.lang = container.dataset.lang;
	}

	setSubmittingState(form, true);

	let response;
	try {
		response = await fetch(config.endpoint, {
			method: 'POST',
			credentials: 'same-origin',
			headers: {
				'Content-Type': 'application/json',
				'Accept': 'application/json',
				'X-WP-Nonce': config.nonce || '',
			},
			body: JSON.stringify(payload),
		});
	} catch (err) {
		setSubmittingState(form, false);
		showFormError((config.strings && config.strings.networkError) || 'Network error. Please try again.');
		return;
	}

	let data = null;
	try { data = await response.json(); } catch (_) { /* ignore */ }

	if (response.ok && data && data.success) {
		if (container && data.html) swapForSuccess(container, data.html);
		return;
	}

	setSubmittingState(form, false);

	if (data && data.errors) {
		const errors = data.errors;
		let firstInvalid = null;
		Object.keys(errors).forEach((field) => {
			if (field === '_form') return;
			const el = setFieldError(form, field, errors[field]);
			if (!firstInvalid) firstInvalid = el;
		});
		if (errors._form) showFormError(errors._form);
		if (firstInvalid) {
			firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
			if (typeof firstInvalid.focus === 'function') {
				try { firstInvalid.focus({ preventScroll: true }); } catch (_) { firstInvalid.focus(); }
			}
		}
	} else {
		showFormError((config.strings && config.strings.submissionFailed) || 'Submission failed. Please try again.');
	}
}

function init() {
	const form = document.getElementById(FORM_ID);
	if (!form) return;
	attachLiveErrorClearing(form);
	form.addEventListener('submit', handleSubmit);
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', init);
} else {
	init();
}
